import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Close, Search } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Checkbox, Modal } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useAssignUsersToTeams } from "../../hooks/useAssignUsersToTeams";
import type { TeamListItem, TeamUser } from "../../types/teams.types";
import { areAllUsersInTeam } from "../../utils/areAllUsersInTeam";
import { filterTeamsBySearch } from "../../utils/filterTeamsBySearch";
import { getUserDisplayName } from "../../utils/getUserDisplayName";
import { AssignTeamsSelectionFooter } from "../AssignUsersToTeamPicker/AssignTeamsSelectionFooter";
import { StyledRow } from "../TeamModal/styles";

const ASSIGN_USER_TO_TEAMS_MODAL_STYLES = {
    content: {
        width: `440px`,
        padding: "0",
        minHeight: "550px",
        maxHeight: "550px",
    },
};

type AssignUserToTeamsModalProps = {
    isOpen: boolean;
    user: TeamUser | undefined;
    teams: readonly TeamListItem[];
    onClose: () => void;
};

export const AssignUserToTeamsModal = ({ isOpen, user, teams, onClose }: AssignUserToTeamsModalProps) => {
    const { translate } = useTranslate();
    const { assignUsersToTeams, isPending } = useAssignUsersToTeams();
    const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!isOpen) {
            setSelectedTeamIds([]);
            setSearchTerm("");
        }
    }, [isOpen, user?.userId]);

    const selectedUserIds = useMemo(() => (user ? [user.userId] : []), [user]);
    const selectedTeamIdSet = useMemo(() => new Set(selectedTeamIds), [selectedTeamIds]);
    const filteredTeams = useMemo(() => filterTeamsBySearch(teams, searchTerm), [teams, searchTerm]);

    const handleToggleTeam = (teamId: number) => {
        setSelectedTeamIds((current) =>
            current.includes(teamId) ? current.filter((id) => id !== teamId) : [...current, teamId],
        );
    };

    const teamRows = filteredTeams.map((team) => {
        const isAlreadyMember = areAllUsersInTeam(team, selectedUserIds);
        const isSelected = selectedTeamIdSet.has(team.teamId);
        const isRowDisabled = isPending || isAlreadyMember;

        return (
            <StyledRow
                key={team.teamId}
                type="button"
                disabled={isRowDisabled}
                onClick={() => handleToggleTeam(team.teamId)}
                aria-pressed={isSelected}
                data-automation={`assign-user-to-teams-row-${team.teamId}`}
            >
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon["500"]}
                    as="span"
                >
                    {team.name ?? ""}
                </Text>
                <Checkbox selected={isSelected} />
            </StyledRow>
        );
    });

    const handleRequestClose = () => {
        if (isPending) {
            return;
        }
        onClose();
    };

    const handleSubmit = async (event: FormEvent<HTMLElement>) => {
        event.preventDefault();

        if (!user || selectedTeamIds.length === 0 || isPending) {
            return;
        }

        const success = await assignUsersToTeams({
            teamIds: selectedTeamIds,
            users: [user],
            teams,
        });

        if (success) {
            onClose();
        }
    };

    if (!user) {
        return null;
    }

    const displayName = getUserDisplayName(user);
    const subtitle = user.email ? `${displayName} · ${user.email}` : displayName;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleRequestClose}
            customStyles={ASSIGN_USER_TO_TEAMS_MODAL_STYLES}
            data-automation="assign-user-to-teams-modal"
        >
            <Stack
                as="form"
                onSubmit={(event) => void handleSubmit(event)}
                noValidate
                flex={1}
            >
                <Modal.Header withBorder>
                    <Stack gap="4px">
                        <Modal.Title data-automation="assign-user-to-teams-modal-title">
                            {translate("si.management.teams.users.assign.modal.title")}
                        </Modal.Title>
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon["300"]}
                            data-automation="assign-user-to-teams-modal-subtitle"
                        >
                            {subtitle}
                        </Text>
                    </Stack>
                </Modal.Header>

                <Modal.Content flex={1}>
                    <Stack
                        gap="12px"
                        padding="16px 0 0"
                    >
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon["400"]}
                            as="span"
                        >
                            {translate("si.management.teams.users.assign.modal.label")}
                        </Text>

                        <TextField
                            value={searchTerm}
                            placeholder={translate("si.management.teams.users.bulk.search_teams")}
                            startAddon={<Search size="sm" />}
                            endAddon={
                                searchTerm.length > 0 ? (
                                    <Close
                                        size="sm"
                                        cursor="pointer"
                                        onClick={() => setSearchTerm("")}
                                    />
                                ) : null
                            }
                            type="text"
                            onChange={setSearchTerm}
                            isDisabled={isPending}
                            data-automation="assign-user-to-teams-search"
                        />

                        <Stack
                            data-automation="assign-user-to-teams-list"
                            maxHeight="280px"
                            minHeight="280px"
                            overflowY="auto"
                            border={`1px solid ${colorsPalettes.carbon["100"]}`}
                            borderRadius="4px"
                        >
                            {teamRows}
                        </Stack>
                    </Stack>
                </Modal.Content>

                <Modal.Footer>
                    <Flex
                        alignItems="center"
                        gap="8px"
                        justifyContent="space-between"
                        flex={1}
                        padding="0 0 0 8px"
                    >
                        <AssignTeamsSelectionFooter
                            selectedCount={selectedTeamIds.length}
                            isPending={isPending}
                            onCancel={handleRequestClose}
                            isSaveDisabled={selectedTeamIds.length === 0 || isPending}
                            cancelDataAutomation="assign-user-to-teams-cancel"
                            saveDataAutomation="assign-user-to-teams-save"
                            saveButtonHtmlType="submit"
                        />
                    </Flex>
                </Modal.Footer>
            </Stack>
        </Modal>
    );
};
